<template>
    <div class="">
        <div class="relative">
            <!-- Trigger Button -->
            <button
                type="button"
                :class="[
                    buttonClass,
                    buttonBaseClass,
                    isOpen && activeButtonClass,
                ]"
                @mousedown.stop.prevent="togglePopover"
                @keydown.enter="togglePopover"
                ref="reference"
            >
                <Icon v-if="icon" :icon="icon" :class="iconClass" />
                {{ buttonText }}
                <slot name="button-content" :is-open="isOpen"></slot>
            </button>
            <!-- Popover Content -->
            <Teleport to="body" :disabled="!teleport">
                <Transition name="fade">
                    <div
                        v-if="isOpen"
                        ref="floating"
                        :class="popoverClass"
                        :style="{
                            ...floatingStyles,
                        }"
                        v-click-outside="handleClickOutside"
                    >
                        <slot v-bind="slotProps"></slot>
                    </div>
                </Transition>
            </Teleport>
        </div>
    </div>
</template>
<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/vue';
import Icon from '@/components/ui/icon/icon.vue';
const props = defineProps({
    buttonText: {
        type: String,
        default: '',
    },
    icon: {
        type: String,
        default: '',
    },
    iconClass: {
        type: String,
        default: '',
    },
    buttonClass: {
        type: String,
        default: 'btn-base btn-transparent',
    },
    activeButtonClass: {
        type: String,
        default: '',
    },
    popoverClass: {
        type: String,
        default:
            'bg-white dark:bg-slate-800 rounded-lg shadow-lg dark:shadow-slate-900/50 min-w-64 z-30 outline outline-1 outline-offset-[-1px] outline-slate-200 dark:outline-slate-700 p-2',
    },
    placement: {
        type: String,
        default: 'bottom-start',
    },
    offsetDistance: {
        type: Number,
        default: 8,
    },
    closeOnSelect: {
        type: Boolean,
        default: true,
    },
    teleport: {
        type: Boolean,
        default: false,
    },
});

// State
const isOpen = ref(false);

// Refs for floating UI
const reference = ref(null);
const floating = ref(null);

const { floatingStyles } = useFloating(reference, floating, {
    placement: props.placement,
    strategy: props.teleport ? 'fixed' : 'absolute',
    middleware: [
        offset(props.offsetDistance),
        flip({ padding: 8 }),
        shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
});

// Computed for conditional classes
const buttonBaseClass = computed(() => {
    return props.icon && !props.buttonText ? 'p-2' : '';
});

// Methods
const handleClickOutside = (event) => {
    if (reference.value && reference.value.contains(event.target)) {
        return;
    }

    if (floating.value && floating.value.contains(event.target)) {
        if (props.closeOnSelect && isOpen.value) {
            closePopover();
        }
        return;
    }

    if (isOpen.value) {
        closePopover();
    }
};

const togglePopover = () => {
    isOpen.value = !isOpen.value;
};

const closePopover = () => {
    if (isOpen.value) {
        isOpen.value = false;
    }
};

defineExpose({
    open: () => {
        isOpen.value = true;
    },
    close: closePopover,
    toggle: togglePopover,
});

const slotProps = {
    closePopover,
};

const handleKeydown = (event) => {
    if (event.key === 'Escape' && isOpen.value) {
        closePopover();
        reference.value?.focus();
    }
};

onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
});
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
